import globalWindow from '@shared/core/globals';

export const newrelic = globalWindow.newrelic;
function isConsoleLoggingEnabled() {
  return typeof window !== 'undefined' && window.location.hash.toLowerCase().includes('consolelogging');
}

const isError = (err: unknown): err is Error => err instanceof Error;

function logToConsoleIfEnabled(method: 'log' | 'error', ...restParams: unknown[]) {
  if (method === 'error' || isConsoleLoggingEnabled()) {
    // eslint-disable-next-line no-console
    console[method]('[Logger]', ...restParams);
  }
}

/**
 * Send a custom Page Action to New Relic.
 * e.g. `addAction('UserLogin', { userId: '12345', success: true })`
 * @param actionName The name of the action to log.
 * @param value The value associated with the action. (optional)
 */
// TODO: Tighten `value` type to EventAttributes once existing callers (28 call sites) are fixed to flatten arrays/objects
export function addAction(actionName: string, value?: Record<string, unknown>): void {
  const valueWithPrefixes: Record<string, unknown> = {};

  for (const key in value) {
    valueWithPrefixes[`${actionName}_${key}`] = value[key];
  }

  // log to console without property prefixes to be easier to consume
  logToConsoleIfEnabled('log', actionName, value);
  newrelic?.addPageAction(actionName, valueWithPrefixes);
}

export function addError(error: unknown, context?: object) {
  let errorValue;

  if (isError(error)) {
    errorValue = error;
  } else if (typeof error === 'string') {
    errorValue = new Error(error);
  } else {
    errorValue = new Error('unknown');
  }

  logToConsoleIfEnabled('error', errorValue, context);
  newrelic?.noticeError(errorValue, context);
}

type AttributeValue = string | number | boolean | null | undefined;
type EventAttributes = Readonly<Record<string, AttributeValue>>;

/**
 * Set a persistent custom attribute on all subsequent New Relic Browser events
 * (PageView, PageViewTiming, PageAction, etc.) for the current session.
 * Pass an empty string to clear a previously set attribute.
 */
export function setCustomAttribute(key: string, value: string | number): void {
  newrelic?.setCustomAttribute(key, value);
}

/**
 * Canonical New Relic attribute key constants.
 *
 * Centralised here so every runtime context (guest, admin, shop, etc.) that
 * calls `setCustomAttribute` uses the exact same string — no typos or drift.
 *
 * See: doc/improve-guest-site-perf/07-instrument-site-perf-tracking/README.md
 */
export const NR_ATTR_ANALYTICS_ID = 'analyticsId';
export const NR_ATTR_RUNTIME_CONTEXT = 'joy.runtimeContext';
export const NR_ATTR_VIEW = 'joy.view';
export const NR_ATTR_EVENT_HANDLE = 'joy.eventHandle';

/**
 * Record a custom event to New Relic for dashboards and alerts.
 *
 * Use for structured metrics (cache hit rates, request outcomes, performance
 * measurements). NOT a replacement for logging — use console/logger for errors,
 * warnings, and developer debugging.
 *
 * Requires browser agent v1.277.0+ with GenericEvents enabled.
 * Events appear as their own NRQL table: `SELECT * FROM MyEventType`
 */
export function recordEvent(eventType: string, attributes: EventAttributes): void {
  // recordCustomEvent only accepts string | number | boolean — strip nulls/undefined to avoid silent drops
  const cleaned: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value !== null && value !== undefined) {
      cleaned[key] = value;
    }
  }
  logToConsoleIfEnabled('log', eventType, attributes);
  newrelic?.recordCustomEvent(eventType, cleaned);
}
